/**
 * Builds Meta-compatible Advanced Matching user data for the GTM dataLayer.
 * Names are split into first/last and every PII value is SHA-256 hashed
 * (hex, lowercase) exactly as Meta expects: normalized (trimmed, lowercased,
 * phone digits-only with country code) before hashing.
 */

async function sha256(value: string): Promise<string> {
  if (!value) return "";
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) return "";
    const buf = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(value),
    );
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

export function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

export type HashedUserData = {
  em: string; // sha256(email)
  ph: string; // sha256(phone, digits + country code)
  fn: string; // sha256(first name)
  ln: string; // sha256(last name)
  country: string; // sha256("us")
};

/**
 * Returns hashed user data ready for Meta (via GTM). Normalization follows
 * Meta's rules: lowercase, strip whitespace, phone as digits with leading 1
 * for US numbers.
 */
export async function hashUserData(input: {
  fullName: string;
  email: string;
  phone: string;
}): Promise<HashedUserData> {
  const { firstName, lastName } = splitName(input.fullName);
  const email = input.email.trim().toLowerCase();
  let digits = input.phone.replace(/\D/g, "");
  if (digits.length === 10) digits = `1${digits}`;
  const [em, ph, fn, ln, country] = await Promise.all([
    sha256(email),
    sha256(digits),
    sha256(firstName.toLowerCase()),
    sha256(lastName.toLowerCase()),
    sha256("us"),
  ]);
  return { em, ph, fn, ln, country };
}
